// src/hooks/useOrgNotes.js
//
// Realtime subscription to notes attached to a given entity (customer, quote,
// job, or invoice) within a single organization.
//
// Returns notes sorted by:
//   1. isPinned DESC (pinned first)        — populated by 1.11; default false in 1.2
//   2. addedAt DESC  (newest first)
//
// Sorting is intentionally client-side so this query does not require a
// composite index in 1.2. When 1.11 ships pin-at-top at scale we can promote
// to a server-ordered query with the appropriate composite index.
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. This hook subscribes to ALL notes (internal + customer-facing) for the
//      given attachedKey. It is intended for team-internal CRM views where the
//      logged-in org user has read access to both visibility levels per the
//      Firestore rules.
//   2. The customer portal MUST use a different read path that filters via
//      publicAttachments. Do not reuse this hook in any portal-facing view.
//   3. The query is fully scoped to organizations/{orgId}/notes — no
//      cross-org leakage is possible.
//   4. The hook bails out cleanly (empty notes, loading false, no listener)
//      when orgId or attachedKey is missing, so it can be called
//      unconditionally from a parent that may not yet have those values.
//   5. Subscription is unsubscribed on unmount and on input change.

import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

const useOrgNotes = ({ orgId, attachedKey }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId || !attachedKey) {
      setNotes([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, `organizations/${orgId}/notes`),
      where('attachedIndex', 'array-contains', attachedKey)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          // Pinned first
          if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
          // Then newest first. addedAt is a Firestore Timestamp on hydrated
          // docs; toMillis() handles that. Fall back to 0 for pending writes
          // that haven't yet received the server value.
          const at = a.addedAt && typeof a.addedAt.toMillis === 'function' ? a.addedAt.toMillis() : 0;
          const bt = b.addedAt && typeof b.addedAt.toMillis === 'function' ? b.addedAt.toMillis() : 0;
          return bt - at;
        });
        setNotes(docs);
        setLoading(false);
      },
      (err) => {
        console.error('useOrgNotes: subscription error', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [orgId, attachedKey]);

  return { notes, loading, error };
};

export default useOrgNotes;